import { Injectable } from '@angular/core';
import { Position } from '../../helpers/types';

declare var google: any;

export interface GoogleMarkerOptions {
  position: google.maps.LatLng;
  map: google.maps.Map;
  animation?: google.maps.Animation;
  draggable: boolean;
  title?: string;
  visible?: boolean;
  icon?: {
    url: string;
    scaledSize: google.maps.Size;
    anchor?: google.maps.Point;
  };
  clickable?: boolean;
  zIndex?: number;
}

@Injectable({
  providedIn: 'root'
})
export class MapMarkerService {
  private markers: Map<string, google.maps.Marker> = new Map();

  private getDefaultMarkerOptions(): GoogleMarkerOptions {
    return {
      position: new google.maps.LatLng(0, 0),
      map: null as any,
      animation: google.maps.Animation.DROP,
      draggable: false,
      title: "Marker",
      visible: true,
      clickable: true,
      zIndex: 1,
    };
  }

  /**
   * Crée un marqueur avec les options spécifiées
   */
  createMarker(options: Partial<GoogleMarkerOptions>): google.maps.Marker {
    const markerOptions: GoogleMarkerOptions = {
      ...this.getDefaultMarkerOptions(),
      ...options,
    };

    const marker = new google.maps.Marker(markerOptions);
    return marker;
  }

  /**
   * Crée un marqueur à partir d'une position
   */
  createMarkerFromPosition(
    position: Position, 
    map: google.maps.Map, 
    options?: Partial<GoogleMarkerOptions>
  ): google.maps.Marker {
    const markerOptions: GoogleMarkerOptions = {
      ...this.getDefaultMarkerOptions(),
      ...options,
      position: new google.maps.LatLng(position.coords.latitude, position.coords.longitude),
      map,
      title: position.label || "Marker",
    };

    const marker = new google.maps.Marker(markerOptions);
    const markerId = this.generateMarkerId(position);
    this.markers.set(markerId, marker);

    return marker;
  }

  /**
   * Ajoute un marqueur à la carte
   */
  addMarker(marker: google.maps.Marker, map: google.maps.Map): void {
    marker.setMap(map);
  }

  /**
   * Supprime un marqueur de la carte
   */
  removeMarker(marker: google.maps.Marker): void {
    if (marker) {
      google.maps.event.clearInstanceListeners(marker);
      marker.setMap(null);
    }
  }

  /**
   * Supprime un marqueur par ID
   */
  removeMarkerById(markerId: string): void {
    const marker = this.markers.get(markerId);
    if (marker) {
      this.removeMarker(marker);
      this.markers.delete(markerId);
    }
  }

  /**
   * Supprime tous les marqueurs
   */
  clearAllMarkers(): void {
    this.markers.forEach(marker => {
      this.removeMarker(marker);
    });
    this.markers.clear();
  }

  /**
   * Met à jour la position d'un marqueur
   */
  updateMarkerPosition(marker: google.maps.Marker, position: Position): void {
    const newPosition = new google.maps.LatLng(
      position.coords.latitude,
      position.coords.longitude
    );
    marker.setPosition(newPosition);
  }

  /**
   * Met à jour l'icône d'un marqueur
   */
  updateMarkerIcon(marker: google.maps.Marker, iconUrl: string, size?: number): void {
    const iconSize = size || 40;
    marker.setIcon({
      url: iconUrl,
      scaledSize: new google.maps.Size(iconSize, iconSize),
      anchor: new google.maps.Point(iconSize / 2, iconSize / 2),
    });
  }

  /**
   * Ajoute un événement de clic à un marqueur
   */
  addMarkerClickListener(
    marker: google.maps.Marker, 
    callback: (marker: google.maps.Marker, position: Position) => void,
    position: Position
  ): void {
    marker.addListener('click', () => {
      callback(marker, position);
    });
  }

  /**
   * Crée un marqueur avec animation de rebond
   */
  createBouncingMarker(options: Partial<GoogleMarkerOptions>): google.maps.Marker {
    const marker = this.createMarker(options);
    marker.setAnimation(google.maps.Animation.BOUNCE);
    
    // Arrêter l'animation après 1.4 secondes
    setTimeout(() => {
      marker.setAnimation(null);
    }, 1400);

    return marker;
  }

  /**
   * Crée un marqueur personnalisé avec une icône SVG
   */
  createCustomSVGMarker(
    position: Position,
    map: google.maps.Map,
    svgContent: string,
    size: number = 40
  ): google.maps.Marker {
    const iconUrl = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svgContent)}`;
    
    return this.createMarkerFromPosition(position, map, {
      icon: {
        url: iconUrl,
        scaledSize: new google.maps.Size(size, size),
        anchor: new google.maps.Point(size / 2, size / 2),
      },
    });
  }

  /**
   * Crée un marqueur avec une icône par défaut de Google Maps
   */
  createDefaultMarker(
    position: Position,
    map: google.maps.Map,
    title?: string
  ): google.maps.Marker {
    return this.createMarkerFromPosition(position, map, {
      title: title || position.label || "Marker",
    });
  }

  /**
   * Génère un ID unique pour un marqueur
   */
  private generateMarkerId(position: Position): string {
    return `${position.coords.latitude}-${position.coords.longitude}-${Date.now()}`;
  }

  /**
   * Obtient tous les marqueurs
   */
  getAllMarkers(): Map<string, google.maps.Marker> {
    return this.markers;
  }

  /**
   * Obtient un marqueur par ID
   */
  getMarkerById(markerId: string): google.maps.Marker | undefined {
    return this.markers.get(markerId);
  }

  /**
   * Vérifie si un marqueur existe
   */
  hasMarker(markerId: string): boolean {
    return this.markers.has(markerId);
  }

  /**
   * Obtient le nombre total de marqueurs
   */
  getMarkerCount(): number {
    return this.markers.size;
  }
} 